import { lazy } from 'react';
import { ROUTES } from 'config';
import { RouteItem } from 'types/Common';
import RecognitionsListPage from '../pages/recognitions-list';
import RecognitionsSettingsPage from '../pages/recognitions-settings';
import RecognitionsAchievementsPage from '../pages/recognitions-achievements';
import RecognitionsAllocationsPage from '../pages/recognitions-allocations';
import RecognitionsGiversPage from '../pages/recognitions-givers';
import RecognitionsRecipientsPage from '../pages/recognitions-recipients';
import RecognitionsRewardsPage from '../pages/recognitions-rewards';
import RecognitionsRulesPage from '../pages/recognitions-rules';
import ShopInternalItemsPage from '../pages/shop-internal-items';
import ShopInternalItemDetailsPage from '../pages/shop-internal-item-details';
import ShopInternalInventoryPage from '../pages/shop-internal-inventory';
import ShopInternalInventoryDetailsPage from '../pages/shop-internal-inventory-details';
import AdminClientAdministrators from '../pages/admin-client-administrators';
import AdminClientSettings from '../pages/admin-client-settings';
import AdminClientFeatures from '../pages/admin-client-features';
import AdminClientDeposits from '../pages/admin-client-deposits';
import AdminClientDepositEventsPage from '../pages/admin-client-deposit-events';
import AdminClientCompanyBalanceLogsPage from '../pages/admin-client-company-balance-logs';
import AdminShopItemSettings from '../pages/admin-shop-item-settings';
import AdminShopItemPrices from '../pages/admin-shop-item-prices';
import AdminShopItemContent from '../pages/admin-shop-item-content';
import AdminShopItemVouchers from '../pages/admin-shop-item-vouchers';
// import AdminLogsPage from '../pages/admin-logs';
import {
  GlobalPermissionModules,
  LocalPermissionModules,
  PermissionLevels,
} from './Administrators';

const Home = lazy(() => import('../containers/Home/Home'));
const CompanySettings = lazy(
  () => import('../containers/CompanySettings/CompanySettings'),
);
const AdministratorsInner = lazy(
  () =>
    import(
      '../containers/CompanySettings/views/Administrators/AdministratorsInner/AdministratorsInner'
    ),
);
const EmployeesListPage = lazy(
  () => import('../containers/Employee/EmployeesList/EmployeesListPage'),
);
const EmployeesInner = lazy(
  () => import('../containers/Employee/EmployeesInner/EmployeesInner'),
);
const NewsDetailsPage = lazy(
  () => import('../containers/news/NewsDetails/NewsDetailsPage'),
);
const ChangePassword = lazy(
  () => import('../containers/ChangePassword/ChangePassword'),
);
const Dashboard = lazy(
  () => import('../containers/Analytics/Dashboard/Dashboard'),
);
const Login = lazy(() => import('../containers/Login/Login'));
const NewPassword = lazy(() => import('../containers/NewPassword/NewPassword'));
const EmployeesGroupsListPage = lazy(
  () =>
    import('../containers/Groups/EmployeesGroupsList/EmployeesGroupsListPage'),
);
const GroupsInner = lazy(
  () => import('../containers/Groups/GroupsInner/GroupsInner'),
);
const TwoFactor = lazy(() => import('../containers/TwoFactor/TwoFactor'));
const ForgotPassword = lazy(
  () => import('../containers/ForgotPassword/ForgotPassword'),
);
const ResetPassword = lazy(
  () => import('../containers/ResetPassword/ResetPassword'),
);
const Clients = lazy(
  () => import('../containers/Clients/ClientsTable/ClientsTable'),
);
const ClientsAdministrator = lazy(
  () => import('../containers/ClientsAdministrators/ClientsAdministrators'),
);
const BenefitTemplates = lazy(
  () =>
    import('../containers/BenefitTemplate/BenefitTemplates/BenefitTemplates'),
);
const BenefitTemplate = lazy(
  () => import('../containers/BenefitTemplate/BenefitTemplate/BenefitTemplate'),
);
const SupplierPage = lazy(
  () => import('../containers/Suppliers/Supplier/SupplierPage'),
);
const IntegrationLogsPage = lazy(
  () =>
    import('../containers/Integrations/IntegrationLogs/IntegrationLogsPage'),
);
const MarketplacePage = lazy(
  () => import('../containers/Marketplace/MarketplaceDeals/MarketplacePage'),
);
const AlternativeDetails = lazy(
  () =>
    import(
      '../containers/AlternativesOld/AlternativeDetails/AlternativeDetailsPage'
    ),
);
const SurveysListPage = lazy(
  () => import('../containers/Surveys/SurveysList/SurveysListPage'),
);
const SurveyDetailsPage = lazy(
  () => import('../containers/Surveys/SurveyDetails/SurveyDetailsPage'),
);
const SurveyResultsPage = lazy(
  () => import('../containers/Surveys/SurveyResults/SurveyResultsPage'),
);
const CreateAchievementPage = lazy(
  () =>
    import('../containers/Recognition/CreateAchievement/CreateAchievementPage'),
);
const EditAchievementPage = lazy(
  () => import('../containers/Recognition/EditAchievement/EditAchievementPage'),
);
const FeedbackListPage = lazy(
  () => import('../containers/TrustBox/FeedbackList/FeedbackListPage'),
);
const PageNotFound = lazy(
  () => import('../containers/PageNotFound/PageNotFound'),
);
const BenefitDetailsPage = lazy(
  () => import('../containers/BenefitsOld/BenefitDetails/BenefitDetailsPage'),
);
const EmployeeTurnoverCalculatorPage = lazy(
  () => import('../pages/public/employee-turnover-calculator'),
);
const NewsPage = lazy(() => import('../pages/news'));
const NewsCategoryDetailsPage = lazy(
  () => import('../pages/news-category-details'),
);
const AlternativesPage = lazy(() => import('../pages/alternatives'));
const ExpensesPage = lazy(() => import('../pages/expenses'));
const ExternalExpensesPage = lazy(() => import('../pages/external-expenses'));
const AdminReportsPage = lazy(() => import('../pages/admin-reports'));
const AdminOrdersPage = lazy(() => import('../pages/admin-orders'));
const AdminOrderPage = lazy(() => import('../pages/admin-order'));
const AdminDiscountsPage = lazy(() => import('../pages/admin-discounts'));
const AdminDiscountDetailsPage = lazy(
  () => import('../pages/admin-discount-details'),
);
const AdminDiscountCodesPage = lazy(
  () => import('../pages/admin-discount-codes'),
);
const AdminIntegrationsPage = lazy(() => import('../pages/admin-integrations'));
const AdminIntegrationDetailsPage = lazy(
  () => import('../pages/admin-integration-details'),
);
const AdminSuppliersPage = lazy(() => import('../pages/admin-suppliers'));
const AdminShopItems = lazy(() => import('../pages/admin-shop-items'));
const OrdersPage = lazy(() => import('../pages/orders'));
const OrderInternalPage = lazy(() => import('../pages/order-internal'));
const ClaimsPage = lazy(() => import('../pages/claims'));
const ClaimDetailsPage = lazy(() => import('../pages/claim-details'));
const WalletsPage = lazy(() => import('../pages/wallets'));
const LogsPage = lazy(() => import('../pages/logs'));
const BenefitsPage = lazy(() => import('../pages/benefits'));
const DiscountsPage = lazy(() => import('../pages/discounts'));
const AutomationsPage = lazy(() => import('../pages/automations'));
const AutomationDetailsPage = lazy(() => import('../pages/automation-details'));

export const routes = [
  {
    path: ROUTES.root.index,
    component: Home,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.public.employeeTurnoverCalculator,
    component: EmployeeTurnoverCalculatorPage,
    public: true,
  },
  {
    path: ROUTES.benefits.list,
    component: BenefitsPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.benefits.details,
    component: BenefitDetailsPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: '/more/company-settings',
    component: CompanySettings,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Administrators,
      GlobalPermissionModules.OrganisationStructure,
    ],
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: '/more/company-settings/integrations/:id/logs',
    component: IntegrationLogsPage,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Administrators,
      GlobalPermissionModules.OrganisationStructure,
    ],
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.administrators.details,
    component: AdministratorsInner,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Administrators,
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.employees.list,
    component: EmployeesListPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.Employees,
    permissionLevel: PermissionLevels.Local,
  },
  {
    path: ROUTES.employees.details,
    component: EmployeesInner,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.Employees,
    permissionLevel: PermissionLevels.Local,
  },
  {
    path: ROUTES.auth.changePassword,
    component: ChangePassword,
    roles: ['hrAdmin', 'melpAdmin'],
  },
  {
    path: ROUTES.news.list,
    component: NewsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.News,
    permissionLevel: PermissionLevels.Local,
  },
  {
    path: ROUTES.news.categories.create,
    component: NewsCategoryDetailsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.News,
    permissionLevel: PermissionLevels.Local,
  },
  {
    path: ROUTES.news.categories.details,
    component: NewsCategoryDetailsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.News,
    permissionLevel: PermissionLevels.Local,
  },
  {
    path: ROUTES.analytics,
    component: Dashboard,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.Dashboard,
    permissionLevel: PermissionLevels.Local,
  },
  {
    path: ROUTES.logs.list,
    component: LogsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.History,
    permissionLevel: PermissionLevels.Local,
  },
  {
    path: ROUTES.auth.login,
    component: Login,
    public: true,
  },
  {
    path: ROUTES.auth.newPassword,
    component: NewPassword,
    public: true,
  },
  {
    path: ROUTES.employeeGroups.list,
    component: EmployeesGroupsListPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.EmployeeGroups,
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.employeeGroups.details,
    component: GroupsInner,
    roles: ['hrAdmin'],
    module: [
      GlobalPermissionModules.Benefits,
      LocalPermissionModules.Employees,
    ],
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.news.details,
    component: NewsDetailsPage,
    roles: ['hrAdmin'],
    module: LocalPermissionModules.News,
    permissionLevel: PermissionLevels.Local,
  },
  {
    path: ROUTES.auth.twoFactor,
    component: TwoFactor,
    public: true,
  },
  {
    path: ROUTES.auth.forgotPassword,
    component: ForgotPassword,
    public: true,
  },
  {
    path: ROUTES.auth.resetPassword,
    component: ResetPassword,
    public: true,
  },
  {
    path: ROUTES.admin.clients.list,
    component: Clients,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.clients.details.settings,
    component: AdminClientSettings,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.clients.details.features,
    component: AdminClientFeatures,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.clients.details.deposits.list,
    component: AdminClientDeposits,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.clients.details.depositEvents.list,
    component: AdminClientDepositEventsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.clients.details.administrators.list,
    component: AdminClientAdministrators,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.clients.details.balanceLogs.details,
    component: AdminClientCompanyBalanceLogsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.clients.details.administrators.details,
    component: ClientsAdministrator,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.integrations.list,
    component: AdminIntegrationsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.integrations.details,
    component: AdminIntegrationDetailsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.templates.list,
    component: BenefitTemplates,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.templates.details,
    component: BenefitTemplate,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.suppliers.list,
    component: AdminSuppliersPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.suppliers.details,
    component: SupplierPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.shopItems.list,
    component: AdminShopItems,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.shopItems.details.settings,
    component: AdminShopItemSettings,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.shopItems.details.prices,
    component: AdminShopItemPrices,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.shopItems.details.content,
    component: AdminShopItemContent,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.shopItems.details.vouchers,
    component: AdminShopItemVouchers,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.reports,
    component: AdminReportsPage,
    roles: ['melpAdmin'],
  },
  // TODO: uncomment when BE fixes admin logs
  // {
  //   path: ROUTES.admin.logs,
  //   component: AdminLogsPage,
  //   roles: ['melpAdmin'],
  // },
  {
    path: ROUTES.admin.orders.list,
    component: AdminOrdersPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.discounts.list,
    component: AdminDiscountsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.discounts.create,
    component: AdminDiscountDetailsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.discounts.details.settings,
    component: AdminDiscountDetailsPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.discounts.details.codes,
    component: AdminDiscountCodesPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.admin.orders.details,
    component: AdminOrderPage,
    roles: ['melpAdmin'],
  },
  {
    path: ROUTES.shop.external.list,
    component: MarketplacePage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.shop.internal.items.list,
    component: ShopInternalItemsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.shop.internal.items.create,
    component: ShopInternalItemDetailsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.shop.internal.items.details,
    component: ShopInternalItemDetailsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.shop.internal.inventory.list,
    component: ShopInternalInventoryPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.shop.internal.inventory.details,
    component: ShopInternalInventoryDetailsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.discounts.list,
    component: DiscountsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.orders.list,
    component: OrdersPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.Benefits,
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.orders.internal.details,
    component: OrderInternalPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.alternatives.list,
    component: AlternativesPage,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.BenefitAlternatives,
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.alternatives.details,
    component: AlternativeDetails,
    roles: ['hrAdmin'],
    module: GlobalPermissionModules.BenefitAlternatives,
    permissionLevel: PermissionLevels.Global,
  },
  {
    path: ROUTES.surveys.list,
    component: SurveysListPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.surveys.details,
    component: SurveyDetailsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.surveys.results,
    component: SurveyResultsPage,
    roles: ['hrAdmin'],
  },

  {
    path: ROUTES.recognitions.list,
    component: RecognitionsListPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.settings,
    component: RecognitionsSettingsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.achievements.list,
    component: RecognitionsAchievementsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.achievements.create,
    component: CreateAchievementPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.achievements.details,
    component: EditAchievementPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.allocations.list,
    component: RecognitionsAllocationsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.givers.list,
    component: RecognitionsGiversPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.recipients.list,
    component: RecognitionsRecipientsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.rewards.list,
    component: RecognitionsRewardsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.recognitions.rules.list,
    component: RecognitionsRulesPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.trustBox.list,
    component: FeedbackListPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.automations.list,
    component: AutomationsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.automations.create,
    component: AutomationDetailsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.automations.details,
    component: AutomationDetailsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.wallets.list,
    component: WalletsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.expenses.list,
    component: ExpensesPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.expenses.external.list,
    component: ExternalExpensesPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.claims.list,
    component: ClaimsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.claims.details,
    component: ClaimDetailsPage,
    roles: ['hrAdmin'],
  },
  {
    path: ROUTES.root.notFound,
    component: PageNotFound,
    public: true,
  },
] satisfies RouteItem[];
